/******/ (() => { // webpackBootstrap
if (!customElements.get('social-share')) {
  class SocialShare extends HTMLElement {

    connectedCallback() {
      /* ===== Cache the DOM elements and attach the event listeners ===== */
      this.cacheDOMElements();
      this.attachEventListeners();
    }

    cacheDOMElements() {
      this.toggle = this.querySelector('[data-share-toggle]');
      this.menu = this.querySelector('[data-share-menu]');
      this.copyButton = this.querySelector('[data-share-copy-link]');
      this.copyIcon = this.querySelector('[data-share-copy-icon]');
      this.copiedIcon = this.querySelector('[data-share-copied-icon]');
      this.copyText = this.querySelector('[data-share-copy-text]');
    }

    attachEventListeners() {
      if (!this.toggle || !this.menu) return;

      this.onToggleClick = this.toggleMenu.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onMenuItemClick = this.handleMenuItemClick.bind(this);
      this.onReposition = this.positionMenu.bind(this);

      this.toggle.addEventListener('click', this.onToggleClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.menu.querySelectorAll('a, button:not([data-share-copy-link])').forEach((item) => {
        item.addEventListener('click', this.onMenuItemClick);
      });

      if (this.copyButton) {
        this.onCopyClick = this.copyLink.bind(this);
        this.copyButton.addEventListener('click', this.onCopyClick);
      }
    }

    isOpen() {
      return !this.menu.hidden;
    }

    toggleMenu(event) {
      event.stopPropagation();
      this.isOpen() ? this.closeMenu() : this.openMenu();
    }

    openMenu() {
      /* ===== Portal the menu to <body> so it is never clipped by an
         overflow:hidden ancestor (e.g. a product slider / swiper). ===== */
      this.portalMenu();

      this.menu.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.classList.add('social-sharing--open');

      this.positionMenu();

      window.addEventListener('scroll', this.onReposition, { passive: true, capture: true });
      window.addEventListener('resize', this.onReposition, { passive: true });
    }

    closeMenu(focusToggle = false) {
      if (!this.isOpen()) return;

      this.menu.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.classList.remove('social-sharing--open');

      window.removeEventListener('scroll', this.onReposition, { capture: true });
      window.removeEventListener('resize', this.onReposition);

      this.restoreMenu();

      if (focusToggle) this.toggle.focus();
    }

    /* ===== Move the menu to <body> and remember where it came from ===== */
    portalMenu() {
      if (this.menuPortaled) return;

      this.menuPlaceholder = document.createComment('social-share-menu');
      this.menu.parentNode.insertBefore(this.menuPlaceholder, this.menu);

      /* Carry the surrounding colour scheme so the portaled menu keeps its theme. */
      const schemeHost = this.closest('[class*="color-scheme--"]');
      if (schemeHost) {
        this.portedSchemeClass = Array.from(schemeHost.classList).find((c) => c.indexOf('color-scheme--') === 0);
        if (this.portedSchemeClass) this.menu.classList.add(this.portedSchemeClass);
      }

      document.body.appendChild(this.menu);
      this.menu.classList.add('social-sharing__menu--portal');
      this.menuPortaled = true;
    }

    /* ===== Return the menu to its original position in the component ===== */
    restoreMenu() {
      if (!this.menuPortaled) return;

      this.menu.classList.remove('social-sharing__menu--portal');
      if (this.portedSchemeClass) {
        this.menu.classList.remove(this.portedSchemeClass);
        this.portedSchemeClass = null;
      }
      this.menu.style.top = '';
      this.menu.style.left = '';

      if (this.menuPlaceholder && this.menuPlaceholder.parentNode) {
        this.menuPlaceholder.parentNode.insertBefore(this.menu, this.menuPlaceholder);
        this.menuPlaceholder.remove();
      } else {
        this.appendChild(this.menu);
      }

      this.menuPlaceholder = null;
      this.menuPortaled = false;
    }

    /* ===== Compute the best position for the portaled menu ===== */
    positionMenu() {
      if (!this.menuPortaled || !this.isOpen()) return;

      const gap = 6;
      const margin = 12;
      const toggleRect = this.toggle.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;

      /* Close the menu if the toggle has been scrolled out of view. */
      if (toggleRect.bottom < 0 || toggleRect.top > viewportHeight) {
        this.closeMenu();
        return;
      }

      const menuWidth = this.menu.offsetWidth;
      const menuHeight = this.menu.offsetHeight;

      /* --- Vertical placement: prefer below, flip above when it does not fit --- */
      const spaceBelow = viewportHeight - toggleRect.bottom - gap;
      const spaceAbove = toggleRect.top - gap;
      let top;

      if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
        top = toggleRect.bottom + gap;
      } else {
        top = toggleRect.top - gap - menuHeight;
      }
      top = Math.max(margin, Math.min(top, viewportHeight - menuHeight - margin));

      /* --- Horizontal placement: align to the toggle, keep inside the viewport --- */
      let left = toggleRect.left;
      if (left + menuWidth > viewportWidth - margin) {
        left = toggleRect.right - menuWidth;
      }
      left = Math.max(margin, Math.min(left, viewportWidth - menuWidth - margin));

      this.menu.style.top = `${Math.round(top)}px`;
      this.menu.style.left = `${Math.round(left)}px`;
    }

    handleDocumentClick(event) {
      if (this.contains(event.target)) return;
      if (this.menuPortaled && this.menu.contains(event.target)) return;
      this.closeMenu();
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isOpen()) this.closeMenu(true);
    }

    handleMenuItemClick() {
      this.closeMenu();
    }

    copyLink() {
      const url = this.getAttribute('data-permalink');
      if (!url) return;

      const onSuccess = () => this.handleCopySuccess();

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(onSuccess).catch(() => this.fallbackCopy(url, onSuccess));
      } else {
        this.fallbackCopy(url, onSuccess);
      }
    }

    fallbackCopy(url, onSuccess) {
      const tempInput = document.createElement('textarea');
      tempInput.value = url;
      tempInput.setAttribute('readonly', '');
      tempInput.style.position = 'absolute';
      tempInput.style.left = '-9999px';
      document.body.appendChild(tempInput);
      tempInput.select();

      try {
        document.execCommand('copy');
        onSuccess();
      } catch (err) {
        console.log('Failed to copy: ', err);
      }

      document.body.removeChild(tempInput);
    }

    handleCopySuccess() {
      if (!this.copyButton) return;

      const copiedLabel = this.copyButton.getAttribute('data-copied-label');
      const copyLabel = this.copyButton.getAttribute('data-copy-label');

      if (this.copyIcon) this.copyIcon.classList.add('hide-icon');
      if (this.copiedIcon) this.copiedIcon.classList.remove('hide-icon');
      if (this.copyText && copiedLabel) this.copyText.textContent = copiedLabel;

      clearTimeout(this.copyResetTimeout);
      this.copyResetTimeout = setTimeout(() => {
        if (this.copyIcon) this.copyIcon.classList.remove('hide-icon');
        if (this.copiedIcon) this.copiedIcon.classList.add('hide-icon');
        if (this.copyText && copyLabel) this.copyText.textContent = copyLabel;
      }, 1500);
    }

    disconnectedCallback() {
      if (!this.toggle || !this.menu) return;

      /* Make sure a portaled menu never gets orphaned on <body>. */
      this.closeMenu();
      if (this.menuPortaled) this.restoreMenu();
      if (this.menu && this.menu.parentNode === document.body) this.menu.remove();

      this.toggle.removeEventListener('click', this.onToggleClick);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onReposition, { capture: true });
      window.removeEventListener('resize', this.onReposition);
      this.menu.querySelectorAll('a, button:not([data-share-copy-link])').forEach((item) => {
        item.removeEventListener('click', this.onMenuItemClick);
      });

      if (this.copyButton) this.copyButton.removeEventListener('click', this.onCopyClick);
      clearTimeout(this.copyResetTimeout);
    }
  }
  customElements.define('social-share', SocialShare);
}
/******/ })()
;
